import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { viabilityLabel } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { buildReferralLink, copyReferralLink, shareReferralLink } from '@/features/auth/referral';
import { AdminIcon } from '@/features/admin/icons';
import { LocaleToggle } from '@/features/i18n/LocaleToggle';
import { useToast } from '@/features/ui/toast/useToast';

export { ConcoursHubPage as ConcoursPage } from '@/pages/user/ConcoursHubPage';

export function ProjectsPage() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<
    { id: string; title: string; viability: string; documents_complete: boolean; created_at: string; feasibility_score: number | null }[]
  >([]);

  useEffect(() => {
    if (!profile) return;
    void kuunda
      .from('projects')
      .select('id, title, viability, documents_complete, created_at, feasibility_score')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => setRows(data ?? []));
  }, [profile]);

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Hub de navigation vers conception, planification, pilotage et budget.</p>
        </div>
        <Link className="btn btn-primary" to="/app/conception">
          Nouvelle conception
        </Link>
      </div>

      {rows.length === 0 ? (
        <p className="u-empty">Aucun projet. Complétez BMC + business plan dans Conception.</p>
      ) : (
        <div className="u-modules">
          {rows.map((r) => (
            <section className="u-module u-module--navy" key={r.id} style={{ cursor: 'default', minHeight: 0 }}>
              <div className="u-module-top">
                <span className="u-module-step">Projet</span>
                <div className="u-module-icon navy">{r.title.charAt(0)}</div>
              </div>
              <h3>{r.title}</h3>
              <p>
                Créé le {new Date(r.created_at).toLocaleDateString('fr-FR')}
                {r.feasibility_score != null ? ` · score ${r.feasibility_score}` : ''}
              </p>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                <span className={`chip ${r.viability === 'viable' ? 'ok' : 'warn'}`}>
                  {viabilityLabel(r.viability)}
                </span>
                <span className="chip">{r.documents_complete ? 'Documents complets' : 'Documents en cours'}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                <Link className="btn btn-ghost btn-module-link--conception" to="/app/conception">
                  Conception
                </Link>
                <Link className="btn btn-ghost btn-module-link--planification" to="/app/planification">
                  Planification
                </Link>
                <Link className="btn btn-ghost btn-module-link--pilotage" to="/app/pilotage">
                  Pilotage
                </Link>
                <Link className="btn btn-ghost btn-module-link--budget" to="/app/budget">
                  Budget
                </Link>
                <Link className="btn btn-ghost btn-module-link--marketplace" to="/app/marketplace-crm">
                  Marketplace & CRM
                </Link>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

type ReferralRow = {
  id: string;
  konan_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
};

type VoucherRow = {
  id: string;
  quarter_key: string;
  referral_count: number;
  voucher_code: string;
  voucher_amount: number;
  currency: string;
  created_at: string;
};

const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

function profileInitials(first?: string | null, last?: string | null, email?: string | null) {
  const a = (first ?? '').trim().charAt(0);
  const b = (last ?? '').trim().charAt(0);
  if (a || b) return `${a}${b}`.toUpperCase();
  return (email ?? '?').trim().charAt(0).toUpperCase() || '?';
}

function extFromMime(mime: string) {
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/gif') return 'gif';
  return 'jpg';
}

export function ProfilePage() {
  const { profile, refreshProfile, signOut } = useAuth();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState(profile?.first_name ?? '');
  const [lastName, setLastName] = useState(profile?.last_name ?? '');
  const [phone, setPhone] = useState(profile?.phone_number ?? '');
  const [referrals, setReferrals] = useState<ReferralRow[]>([]);
  const [vouchers, setVouchers] = useState<VoucherRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(profile?.first_name ?? '');
    setLastName(profile?.last_name ?? '');
    setPhone(profile?.phone_number ?? '');
    setPreviewUrl(null);
  }, [profile]);

  useEffect(() => {
    if (!profile) return;
    void kuunda.rpc('list_my_referrals').then(({ data }) => {
      setReferrals((data as ReferralRow[]) ?? []);
    });
    void kuunda
      .from('referral_vouchers')
      .select('id, quarter_key, referral_count, voucher_code, voucher_amount, currency, created_at')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => setVouchers((data as VoucherRow[]) ?? []));
  }, [profile]);

  const displayName =
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    profile?.email?.split('@')[0] ||
    'Entrepreneur';
  const avatarSrc = previewUrl || profile?.avatar_url || null;
  const initials = profileInitials(firstName || profile?.first_name, lastName || profile?.last_name, profile?.email);

  async function save() {
    if (!profile) return;
    setSaving(true);
    const { error: err } = await kuunda
      .from('profiles')
      .update({ first_name: firstName.trim(), last_name: lastName.trim(), phone_number: phone.trim() || null })
      .eq('id', profile.id);
    setSaving(false);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success('Profil mis à jour.', 'Compte');
      await refreshProfile();
    }
  }

  async function onAvatarSelected(file: File | null) {
    if (!profile || !file) return;
    if (!AVATAR_TYPES.has(file.type)) {
      toast.error('Formats acceptés : JPG, PNG, WebP ou GIF.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast.error('La photo doit faire moins de 2 Mo.');
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setUploadingAvatar(true);

    try {
      const ext = extFromMime(file.type);
      const path = `${profile.id}/avatar.${ext}`;

      const { data: existing } = await kuunda.storage.from('avatars').list(profile.id);
      const stale = (existing ?? [])
        .map((f) => f.name)
        .filter((name) => name !== `avatar.${ext}`)
        .map((name) => `${profile.id}/${name}`);
      if (stale.length) await kuunda.storage.from('avatars').remove(stale);

      const { error: upErr } = await kuunda.storage.from('avatars').upload(path, file, {
        upsert: true,
        contentType: file.type,
        cacheControl: '3600',
      });
      if (upErr) throw upErr;

      const { data: pub } = kuunda.storage.from('avatars').getPublicUrl(path);
      const avatarUrl = `${pub.publicUrl}?v=${Date.now()}`;

      const { error: updErr } = await kuunda
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', profile.id);
      if (updErr) throw updErr;

      toast.success('Photo de profil mise à jour.', 'Compte');
      await refreshProfile();
      setPreviewUrl(null);
    } catch (err) {
      setPreviewUrl(null);
      toast.error(err instanceof Error ? err.message : 'Upload impossible');
    } finally {
      setUploadingAvatar(false);
      URL.revokeObjectURL(localPreview);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function removeAvatar() {
    if (!profile?.avatar_url) return;
    setUploadingAvatar(true);
    try {
      const { data: listed } = await kuunda.storage.from('avatars').list(profile.id);
      const names = (listed ?? []).map((f) => `${profile.id}/${f.name}`);
      if (names.length) await kuunda.storage.from('avatars').remove(names);

      const { error: updErr } = await kuunda
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', profile.id);
      if (updErr) throw updErr;

      setPreviewUrl(null);
      toast.success('Photo de profil retirée.', 'Compte');
      await refreshProfile();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Suppression impossible');
    } finally {
      setUploadingAvatar(false);
    }
  }

  const referralCode = profile?.konan_id ?? '';
  const shareLink =
    typeof window !== 'undefined' && referralCode ? buildReferralLink(referralCode) : '';

  async function onCopyReferral() {
    if (!shareLink) return;
    const copied = await copyReferralLink(shareLink);
    if (copied) toast.success('Lien copié.', 'Parrainage');
    else toast.error('Impossible de copier le lien.');
  }

  async function onShareReferral() {
    if (!shareLink || !referralCode) return;
    const result = await shareReferralLink(shareLink, referralCode);
    if (result === 'shared') toast.success('Lien partagé.', 'Parrainage');
    else if (result === 'copied') toast.success('Lien copié.', 'Parrainage');
  }

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Identité, photo et lien de parrainage — votre vitrine entrepreneuriale ISBD.</p>
        </div>
      </div>

      <section className="u-profile-hero">
        <div className="u-profile-avatar-wrap">
          <div className={`u-profile-avatar${avatarSrc ? ' has-photo' : ''}`}>
            {avatarSrc ? (
              <img src={avatarSrc} alt={`Photo de ${displayName}`} />
            ) : (
              <span aria-hidden>{initials}</span>
            )}
            {uploadingAvatar ? <div className="u-profile-avatar-busy">…</div> : null}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            hidden
            onChange={(e) => void onAvatarSelected(e.target.files?.[0] ?? null)}
          />
          <div className="u-profile-avatar-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={uploadingAvatar}
              onClick={() => fileRef.current?.click()}
            >
              {avatarSrc ? 'Changer la photo' : 'Ajouter une photo'}
            </button>
            {profile?.avatar_url ? (
              <button
                type="button"
                className="btn btn-ghost"
                disabled={uploadingAvatar}
                onClick={() => void removeAvatar()}
              >
                Retirer
              </button>
            ) : null}
          </div>
          <p className="u-profile-avatar-hint">JPG, PNG, WebP ou GIF — 2 Mo max.</p>
        </div>

        <div className="u-profile-hero-meta">
          <p className="u-profile-eyebrow">Compte entrepreneur</p>
          <h3 className="u-profile-name">{displayName}</h3>
          <p className="u-profile-email">{profile?.email}</p>
          <div className="u-profile-tags">
            {profile?.konan_id ? <span className="chip">{profile.konan_id}</span> : null}
            <span className="chip">Membre ISBD</span>
          </div>
        </div>
      </section>

      <div className="u-profile-grid">
        <section className="u-card u-profile-form-card">
          <div className="u-section-head">
            <h3 className="u-section-title" style={{ marginTop: 0 }}>
              Informations personnelles
            </h3>
            <p className="u-section-lead">Ces infos apparaissent dans votre espace et vos échanges avec l’équipe.</p>
          </div>

          <div className="field">
            <label htmlFor="profile-konan">ID KONAN</label>
            <input id="profile-konan" value={profile?.konan_id ?? '—'} disabled />
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="profile-first">Prénom</label>
              <input
                id="profile-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div className="field">
              <label htmlFor="profile-last">Nom</label>
              <input
                id="profile-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="profile-email">Email</label>
            <input id="profile-email" value={profile?.email ?? ''} disabled autoComplete="email" />
          </div>
          <div className="field">
            <label htmlFor="profile-phone">Téléphone</label>
            <input
              id="profile-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              placeholder="+225 …"
            />
          </div>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </button>
        </section>

        <section className="u-card u-profile-referral-card">
          <div className="u-section-head">
            <h3 className="u-section-title" style={{ marginTop: 0 }}>
              Parrainage
            </h3>
            <p className="u-section-lead">Invitez d’autres entrepreneurs et suivez vos filleuls.</p>
          </div>

          <div className="u-profile-stat">
            <p className="u-profile-stat-value">{referrals.length}</p>
            <p className="u-profile-stat-label">filleul(s) sur ce cycle</p>
          </div>

          {referralCode ? (
            <>
              <div className="field">
                <label htmlFor="profile-ref-code">Votre code</label>
                <input id="profile-ref-code" value={referralCode} readOnly aria-readonly />
              </div>
              <div className="field">
                <label htmlFor="profile-ref-link">Lien d’invitation</label>
                <input id="profile-ref-link" value={shareLink} readOnly aria-readonly />
              </div>
              <div className="u-actions">
                <button type="button" className="btn btn-ghost" onClick={() => void onCopyReferral()}>
                  Copier
                </button>
                <button type="button" className="btn btn-primary" onClick={() => void onShareReferral()}>
                  Partager
                </button>
              </div>
            </>
          ) : (
            <p className="muted">Votre code parrainage sera disponible sous peu.</p>
          )}

          <p className="muted" style={{ marginTop: '0.85rem', marginBottom: 0 }}>
            Le compteur est réinitialisé à chaque réabonnement.
          </p>

          {referrals.length > 0 ? (
            <ul className="u-list" style={{ marginTop: '0.85rem' }}>
              {referrals.map((r) => (
                <li key={r.id} className="u-list-item">
                  <div>
                    <strong>{r.konan_id ?? r.id.slice(0, 8)}</strong>
                    <div className="muted" style={{ fontSize: '0.88rem' }}>
                      {[r.first_name, r.last_name].filter(Boolean).join(' ') || r.email}
                      {r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString('fr-FR')}` : ''}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>

      {vouchers.length > 0 ? (
        <section className="u-card">
          <h3 className="u-section-title" style={{ marginTop: 0 }}>
            Bons d’achat
          </h3>
          <ul className="u-list">
            {vouchers.map((v) => (
              <li key={v.id} className="u-list-item">
                <div>
                  <strong>{v.voucher_code}</strong>
                  <div className="muted" style={{ fontSize: '0.88rem' }}>
                    {v.quarter_key} · {v.referral_count} filleul(s) · {v.voucher_amount} {v.currency}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="u-card u-profile-settings" aria-labelledby="profile-settings-title">
        <div className="u-section-head">
          <h3 id="profile-settings-title" className="u-section-title" style={{ marginTop: 0 }}>
            Paramètres du compte
          </h3>
          <p className="u-section-lead">Langue, sécurité, abonnement et session.</p>
        </div>

        <ul className="u-settings-list">
          <li className="u-settings-row">
            <span className="u-settings-icon" aria-hidden>
              <AdminIcon name="globe" size={18} />
            </span>
            <div className="u-settings-body">
              <p className="u-settings-title">Langue</p>
              <p className="u-settings-desc">Interface française ou anglaise</p>
            </div>
            <LocaleToggle className="u-settings-locale" />
          </li>

          <li>
            <Link className="u-settings-row u-settings-row--link" to="/app/mot-de-passe">
              <span className="u-settings-icon" aria-hidden>
                <AdminIcon name="lock" size={18} />
              </span>
              <div className="u-settings-body">
                <p className="u-settings-title">Mot de passe</p>
                <p className="u-settings-desc">Modifier le mot de passe de connexion</p>
              </div>
              <span className="u-settings-chevron" aria-hidden>
                <AdminIcon name="chevronRight" size={16} />
              </span>
            </Link>
          </li>

          <li>
            <Link className="u-settings-row u-settings-row--link" to="/app/abonnement">
              <span className="u-settings-icon u-settings-icon--gold" aria-hidden>
                <AdminIcon name="credit" size={18} />
              </span>
              <div className="u-settings-body">
                <p className="u-settings-title">Abonnement</p>
                <p className="u-settings-desc">Consulter ou changer votre offre</p>
              </div>
              <span className="u-settings-chevron" aria-hidden>
                <AdminIcon name="chevronRight" size={16} />
              </span>
            </Link>
          </li>

          <li>
            <button
              type="button"
              className="u-settings-row u-settings-row--link u-settings-row--danger"
              onClick={() => void signOut()}
            >
              <span className="u-settings-icon u-settings-icon--danger" aria-hidden>
                <AdminIcon name="logout" size={18} />
              </span>
              <div className="u-settings-body">
                <p className="u-settings-title">Déconnexion</p>
                <p className="u-settings-desc">Quitter votre session sur cet appareil</p>
              </div>
              <span className="u-settings-chevron" aria-hidden>
                <AdminIcon name="chevronRight" size={16} />
              </span>
            </button>
          </li>
        </ul>
      </section>
    </div>
  );
}
